import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { ArrowRight, Gift, Trophy } from 'lucide-react';

const programs = [
  {
    icon: Trophy,
    audience: 'Organisateurs',
    gradient: 'from-blue-600 to-indigo-600',
    lightBg: 'bg-blue-50',
    lightText: 'text-blue-700',
    accentText: 'text-blue-600',
    badge: 'Crédit commission',
    title: 'Parrainez des organisateurs',
    description:
      "Recommandez BoulesConnect à d'autres organisateurs. Chaque filleul qui génère sa première commission vous offre 30 € de crédit — jusqu'à 150 € au total.",
    reward: '30 € de crédit commission',
    rewardValue: '30 €',
    cap: '5 filleuls max',
    capValue: '150 € de crédit',
    cta: 'Découvrir le programme',
    route: ROUTES.PARRAINAGE_ORGANISATEUR,
    highlights: ['30 € par filleul validé', '5 filleuls maximum', 'Déclenchement à la 1re commission'],
  },
];

export default function ParrainageIndex() {
  return (
    <section className='pt-32 pb-20 px-6'>
      <PageMeta
        title='Programmes de parrainage'
        description='Découvrez les programmes de parrainage BoulesConnect : parrainez des organisateurs, clubs ou joueurs et gagnez des récompenses exclusives.'
      />
      {/* Hero */}
      <div className='max-w-4xl mx-auto text-center mb-20'>
        <div className='inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 font-semibold text-sm px-4 py-2 rounded-full mb-6'>
          <Gift className='w-4 h-4' />
          Programmes de Parrainage
        </div>
        <h1 className='text-4xl md:text-5xl font-extrabold mb-6'>
          <span className='bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent'>
            Partagez BoulesConnect,
          </span>
          <br />
          <span className='text-slate-900'>tout le monde gagne</span>
        </h1>
        <p className='text-lg text-slate-600 max-w-2xl mx-auto'>
          Parrainez des organisateurs et gagnez des crédits commission. D'autres programmes arrivent bientôt pour les
          clubs et les joueurs.
        </p>
      </div>

      {/* Program Cards */}
      <div className='max-w-lg mx-auto mb-20'>
        {programs.map((prog, i) => (
          <div
            key={i}
            className='bg-white rounded-3xl border border-slate-100 overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-shadow'
          >
            {/* Header */}
            <div className={`bg-gradient-to-br ${prog.gradient} p-7 text-white`}>
              <div className='flex items-start justify-between mb-4'>
                <div className='w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center'>
                  <prog.icon className='w-6 h-6' />
                </div>
                <span className='text-xs font-bold bg-white/20 px-3 py-1 rounded-full'>{prog.badge}</span>
              </div>
              <h3 className='text-xl font-bold mb-1'>{prog.title}</h3>
              <p className='text-sm opacity-80'>{prog.audience}</p>
            </div>

            {/* Body */}
            <div className='p-7 flex flex-col flex-1'>
              <p className='text-slate-600 text-sm leading-relaxed mb-6'>{prog.description}</p>

              {/* Reward highlight */}
              <div className={`${prog.lightBg} rounded-2xl p-4 mb-6`}>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className={`text-xs font-semibold ${prog.lightText} mb-0.5`}>{prog.cap}</p>
                    <p className='text-xs text-slate-500'>{prog.capValue}</p>
                  </div>
                  <div className='text-right'>
                    <p className={`text-2xl font-extrabold ${prog.accentText}`}>{prog.rewardValue}</p>
                    <p className='text-xs text-slate-500'>{prog.reward}</p>
                  </div>
                </div>
              </div>

              {/* Feature list */}
              <ul className='space-y-2 mb-8 flex-1'>
                {prog.highlights.map((item, j) => (
                  <li key={j} className='flex items-start gap-2 text-sm text-slate-600'>
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${prog.accentText.replace('text', 'bg')} mt-1.5 flex-shrink-0`}
                    />
                    {item}
                  </li>
                ))}
              </ul>

              <Link
                to={prog.route}
                className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r ${prog.gradient} text-white font-semibold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity`}
              >
                {prog.cta}
                <ArrowRight className='w-4 h-4' />
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* How it works — generic */}
      <div className='max-w-4xl mx-auto mb-20'>
        <div className='bg-slate-50 rounded-3xl p-10'>
          <h2 className='text-2xl font-bold text-slate-900 text-center mb-8'>Le principe universel</h2>
          <div className='grid md:grid-cols-3 gap-8 text-center'>
            {[
              {
                step: '1',
                title: 'Obtenez votre code',
                desc: 'Chaque compte reçoit un code de parrainage unique disponible depuis votre tableau de bord.',
              },
              {
                step: '2',
                title: 'Partagez-le',
                desc: "Envoyez votre code à vos contacts. Le filleul l'utilise lors de son inscription sur BoulesConnect.",
              },
              {
                step: '3',
                title: 'Récoltez vos gains',
                desc: 'Une fois le filleul actif, votre récompense est créditée automatiquement selon le programme.',
              },
            ].map((step, i) => (
              <div key={i}>
                <div className='w-12 h-12 bg-indigo-600 text-white font-bold text-lg rounded-2xl flex items-center justify-center mx-auto mb-4'>
                  {step.step}
                </div>
                <h3 className='font-bold text-slate-900 mb-2'>{step.title}</h3>
                <p className='text-slate-600 text-sm leading-relaxed'>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Anti-abuse note */}
      <div className='max-w-3xl mx-auto mb-20'>
        <div className='bg-amber-50 border border-amber-200 rounded-2xl p-6'>
          <h3 className='font-bold text-amber-800 mb-2'>Règles communes à tous les programmes</h3>
          <ul className='space-y-1 text-sm text-amber-700'>
            <li>• Le filleul doit être un nouvel utilisateur BoulesConnect (jamais inscrit)</li>
            <li>• La récompense n'est créditée qu'après une première commission réelle du filleul</li>
            <li>• Un même compte ne peut pas se parrainer lui-même ni parrainer des comptes liés</li>
            <li>• Les récompenses sont plafonnées par programme et par année civile (voir chaque programme)</li>
          </ul>
        </div>
      </div>

      {/* CTA */}
      <div className='max-w-4xl mx-auto'>
        <div className='bg-gradient-to-r from-indigo-600 to-blue-600 rounded-3xl p-10 text-center text-white'>
          <h2 className='text-3xl font-bold mb-4'>Lancez-vous</h2>
          <p className='text-indigo-100 mb-8 max-w-xl mx-auto'>
            Inscrivez-vous gratuitement et accédez à votre code de parrainage depuis votre tableau de bord.
          </p>
          <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              to={ROUTES.REGISTER}
              className='bg-white text-indigo-600 font-bold px-8 py-3 rounded-xl hover:bg-indigo-50 transition-colors'
            >
              Créer un compte gratuit
            </Link>
            <Link
              to={ROUTES.LOGIN}
              className='border-2 border-white/30 text-white font-bold px-8 py-3 rounded-xl hover:bg-white/10 transition-colors'
            >
              J'ai déjà un compte
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
